import invariant from 'tiny-invariant';
import { ElementSizePolicyType } from './size_policy_type';
import { sortedNonDefaultBreakpoints, getBreakpointSpecifiedValue } from './breakpoint_utils';

const aspectRatioReal = (aspectRatio) => aspectRatio.h / aspectRatio.w;

const widthCSS = (width) => {
  if (width === 'auto' || width === 'unset' || width === 'initial' || width === 'inherit' ||
      width === 'min-content' || width === 'max-content' || width.startsWith('fit-content')) {
    return `width: ${width};`;
  }
  return `width: calc(${width});`;
};

const heightCSS = (height) => {
  if (height === 'auto' || height === 'unset' || height === 'initial' || height === 'inherit' ||
      height === 'min-content' || height === 'max-content' || height.startsWith('fit-content')) {
    return `height: ${height};`;
  }
  return `height: calc(${height});`;
};

const mapValues = (object, keyToValueFn) => {
  const keys = Object.keys(object);
  const result = {};
  for (let i = 0; i < keys.length; ++i) {
    result[keys[i]] = keyToValueFn(keys[i]);
  }
  return result;
};

// Returns an array of breakpointFlags induced if the screen size was breakpoint pixels wide.
const inducedBreakpointFlags = (breakpoints, breakpoint) => {
  const breakpointNumber = parseInt(breakpoint, 10);
  const flags = [];
  for (let i = 0; i < breakpoints.length; ++i) {
    flags.push(breakpointNumber >= parseInt(breakpoints[i], 10));
  }
  return flags;
};

export const getSizingCSS = (policy) => {
  const normalizedPolicy = normalizePolicy(policy);
  invariant(normalizedPolicy.type === ElementSizePolicyType.FULLY_SPECIFIED,
    'Normalized policies are expected to be fully specified.');

  const defaultWidth = normalizedPolicy.widths.default;
  const defaultHeight = normalizedPolicy.heights.default;

  // Generate the combined breakpoints.
  const widthBreakpoints = sortedNonDefaultBreakpoints(normalizedPolicy.widths);
  const heightBreakpoints = sortedNonDefaultBreakpoints(normalizedPolicy.heights);
  const breakpoints = [...new Set(widthBreakpoints.concat(heightBreakpoints))];
  breakpoints.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  const defaultCss = `
    ${widthCSS(defaultWidth)}
    ${heightCSS(defaultHeight)}
  `;

  return defaultCss + (breakpoints.map(breakpoint => {
    // Calculate the width, height, and margin for this specific breakpoint.
    const breakpointFlags = inducedBreakpointFlags(breakpoints, breakpoint);
    const width = getBreakpointSpecifiedValue(breakpoints, breakpointFlags, normalizedPolicy.widths);
    const height = getBreakpointSpecifiedValue(breakpoints, breakpointFlags, normalizedPolicy.heights);
    return `
      @media screen and (min-width: ${breakpoint}px) {
        ${widthCSS(width)}
        ${heightCSS(height)}
      }
    `;
  }).join('\n'));
};

export const getSizingCSSForImg = (policy) => {
  // If the CSS is being specified for an img tag, heights need to be set to auto.
  return getSizingCSS({
    type: ElementSizePolicyType.FULLY_SPECIFIED,
    widths: policy.widths,
    heights: mapValues(policy.widths, breakpoint => 'auto')
  });
};

export const normalizePolicy = (policy) => {
  if (policy.type === ElementSizePolicyType.FIXED_ASPECT_RATIO) {
    return {
      type: ElementSizePolicyType.FULLY_SPECIFIED,
      widths: policy.widths,
      heights: mapValues(policy.widths, breakpoint =>
        `(${policy.widths[breakpoint]}) * ${aspectRatioReal(policy.aspectRatio)}`)
    };
  }
  if (policy.type === ElementSizePolicyType.VARIABLE_ASPECT_RATIO) {
    return {
      type: ElementSizePolicyType.FULLY_SPECIFIED,
      widths: policy.widths,
      heights: mapValues(policy.widths, breakpoint =>
        `(${policy.widths[breakpoint]}) * ${aspectRatioReal(policy.aspectRatios[breakpoint])}`)
    };
  }
  return policy;
};
